import {
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  StudentServiceIndex,
  StudentServiceIndexEditResource,
  StudentServiceIndexViewResource,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentServiceIndexPageBody } from "./StudentServiceIndexPageBody";
import { StudentServiceIndexPageHeader } from "./StudentServiceIndexPageHeader";

const routeName = "index";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

export const studentServiceIndexPageRoute: PageRoute<Params> = {
  handle: "Service Index",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const StudentServiceIndexPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentService;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const [index, setIndex] = useState<StudentServiceIndex>({} as StudentServiceIndex);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [index] });
  const [viewResource, setViewResource] = useState<StudentServiceIndexViewResource>({});
  const [editResource, setEditResource] = useState<StudentServiceIndexEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.studentServiceIndexes.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.studentServiceIndexes.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${studentServiceIndexPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`
    );

  const fetch = (id: number) => {
    ApiService.studentServiceIndexes.get(id).then((response) => {
      setIndex(response);
      resetUnsavedChanges([response]);
    });
  };

  const pageHeader = (
    <StudentServiceIndexPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentServiceIndexPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      index={index}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentServiceIndexPageBody
      rootPermission={ROOT_PERMISSION}
      crudMode={params.crudMode}
      index={index}
      setIndex={setIndex}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
      onEdit={() => reroute(params.id, "edit")}
    />
  );

  return (
    <>
      {params.screenType === "standard" ? (
        <Page isForm header={pageHeader}>
          <Card>{pageBody}</Card>
        </Page>
      ) : (
        <Dialog
          isForm
          open={true}
          onClose={() => handleClose(navigate, unsavedChanges)}
          maxWidth="lg"
          header={pageHeader}
          content={pageBody}
        />
      )}

      <Outlet />
    </>
  );
};
